import { cloneDeep } from 'lodash';
import moment from 'moment';
import {
  ADMIN_ROLE,
  FILTER_OPERATORS,
  ORG_ADMIN_ROLE,
  PROCESSOR_ROLE,
  SERVICES_SORT_BY_OPTIONS,
  USA_STATES,
  INDIVIDUAL_ORDERS,
  SALES_PERSON_ROLE
} from './constants';

const firebaseErrors = {
  'auth/email-already-exists': 'The provided email is already in use by an existing user.',
  'auth/invalid-email': 'The provided value for the email is invalid.',
  'auth/invalid-password': 'The provided value for the password is invalid.',
  'auth/invalid-phone-number': 'The provided value for the phone number is invalid.',
  'auth/phone-number-already-exists': 'The provided phone number is already in use by an existing user.',
  'auth/user-not-found': 'There is no existing user corresponding to the provided email.',
  'auth/wrong-password': 'Provided email/password do not match',
  'auth/email-already-in-use': 'The provided email is already in use by an existing user.',
  'auth/weak-password': 'Password should be at least 6 characters'
};

export const getErrorMessageFromFirebase = e => {
  return firebaseErrors[e.code] || e.message || 'Oops, Something went wrong!';
};

/** Quill's empty document is still markup (e.g. `<p><br></p>`). Treat that as ''. */
export const normalizeRichTextValue = html => {
  if (html == null || typeof html !== 'string') return '';
  const text = html
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .trim();
  return text ? html : '';
};

export const formatCurrency = (value, emptyDisplay = '-') => {
  if (value == null || value === '' || Number.isNaN(Number(value))) {
    return emptyDisplay;
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value));
};

export const scrollToId = (id, delay = 200) => {
  setTimeout(() => {
    const elem = document.getElementById(id) || document.getElementsByName(id)[0];
    if (elem) elem.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, delay);
};

export const isAdmin = role => role === 'Admin';
export const isOrgAdmin = role => role === 'Org Admin';
export const isStaff = role => ['Admin', 'Processor', 'Sales Person'].includes(role);
export const isClient = role => ['Org Admin', 'Org Staff'].includes(role);
export const isSalesPerson = role => role === 'Sales Person';

export const isQualiaOrganization = organizationOrName => {
  const name =
    typeof organizationOrName === 'string' ? organizationOrName : organizationOrName?.name;
  return /qualia/i.test(name || '');
};

export const canViewCollectedFees = currentUserInfo =>
  isAdmin(currentUserInfo?.role) &&
  !!currentUserInfo?.email &&
  currentUserInfo?.hasFinancialIntelligenceAccess;

export const getNameForFormValues = value => {
  if (value === 'temporaryKey') return 'OTP';
  if (value === 'createdAt') return 'Added On';

  return convertCamelamelToNormalCase(value);
};

export const getValueForFormName = name => {
  if (name === 'OTP') return 'temporaryKey';
  if (name === 'Added On') return 'createdAt';

  return convertNormalToCamelCase(name);
};

export const findFilterObjectForAdmin = (usersFromOrganization, entityName, role, orgId) => {
  if (entityName === 'Users' || entityName === 'Organizations') {
    return !usersFromOrganization
      ? entityName === 'Users'
        ? role === ADMIN_ROLE
          ? { role: [ADMIN_ROLE, PROCESSOR_ROLE, SALES_PERSON_ROLE] }
          : role === ORG_ADMIN_ROLE
            ? { organization: orgId }
            : {}
        : {}
      : { organization: usersFromOrganization?._id };
  }
};

export const findGeneralFilterObjectForEtityCrud = filterObject => {
  return { ...filterObject };
};

export const createUserObjectFromLocalStorage = fields => {
  const userObject = {};

  fields.forEach(field => {
    userObject[field.split('-')[1]] = localStorage.getItem(field) ?? '';
  });

  return userObject;
};

export const getUserFromLocalstrorage = () => {
  return JSON.parse(localStorage.getItem('user'));
};

export const convertNormalToCamelCase = string => {
  const splittedString = string.split(' ');

  return splittedString
    .map((str, idx) => (idx === 0 ? str.toLowerCase() : str[0].toUpperCase() + str.slice(1)))
    .join('');
};

export const convertCamelamelToNormalCase = string => {
  const result = string.replace(/([A-Z])/g, ' $1');
  const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
  return finalResult;
};

export const logout = history => {
  localStorage.clear();
  history.push('/login');
};

export const getUserFirstName = name => {
  if (name) return name.split(' ')[0];

  return name;
};

export const onUpdateApiRequestData = (entityName, updateEndpoint, itemActionMeta, form) => {
  if (isUserOrOrganizationEntity(entityName)) {
    return {
      endpoint: entityName === 'Users' ? `${updateEndpoint}` : `${updateEndpoint}/${itemActionMeta?.item?._id}`,
      requestBody: {
        ...form,
        _id: itemActionMeta?.item?._id,
        organization: itemActionMeta?.item?.organization?._id,
        email: form?.email || itemActionMeta?.item?.email,
        defaultProcessorFor: ![PROCESSOR_ROLE, ADMIN_ROLE].includes(form?.role) ? [] : form?.defaultProcessorFor,
        lienSearchDefaultCounties: ![PROCESSOR_ROLE, ADMIN_ROLE].includes(form?.role)
          ? []
          : form?.lienSearchDefaultCounties,
        isEnabled: typeof form?.isEnabled === 'boolean' ? form?.isEnabled : form?.isEnabled === 'Enable'
      }, // For Updating Organization and [Admin,Processor]  organization will be null and For Organization defaultProcessorFor will be null
      method: entityName === 'Users' ? 'POST' : 'PUT'
    };
  }

  return {
    endpoint: `${updateEndpoint}/${itemActionMeta?.item?._id}`,
    requestBody: { ...form },
    method: 'PUT'
  };
};

export const onUpdateStatusOrDeleteApiRequestData = (entityName, updateEndpoint, toBeDeletedItem, endpoint) => {
  if (toBeDeletedItem?.mode === 'ED') {
    return {
      endpoint: entityName === 'Users' ? `${updateEndpoint}` : `${updateEndpoint}/${toBeDeletedItem?._id}`,
      requestBody: {
        _id: toBeDeletedItem?._id,
        isEnabled: toBeDeletedItem?.isEnabled,
        email: toBeDeletedItem?.email,
        organization: toBeDeletedItem?.organization?._id
      },
      method: entityName === 'Users' ? 'POST' : 'PUT'
    };
  }

  return {
    endpoint: `${endpoint}/${toBeDeletedItem?._id}`,
    requestBody: {},
    method: 'Delete'
  };
};

export const onCreateApiRequestData = (entityName, endpoint, form, usersFromOrganization) => {
  if (isUserOrOrganizationEntity(entityName)) {

    if (entityName === 'Organizations') {
      // Structure newAdmin object from form data
      const {
        'newAdmin.name': adminName,
        'newAdmin.email': adminEmail,
        'newAdmin.phone': adminPhone,
        ...organizationData
      } = form;
      const requestBody = {
        ...organizationData
      };

      if (adminName || adminEmail) {
        requestBody.newAdmin = {
          name: adminName || '',
          email: adminEmail || '',
          phone: adminPhone || '',
          defaultProcessorFor: []
        };
      }

      return {
        endpoint: endpoint,
        requestBody
      };
    }

    return {
      endpoint: endpoint,
      requestBody: usersFromOrganization
        ? {
          ...form,
          organization: usersFromOrganization?._id
        }
        : {
          ...form,
          defaultProcessorFor: ![PROCESSOR_ROLE, ADMIN_ROLE].includes(form?.role) ? [] : form?.defaultProcessorFor,
          lienSearchDefaultCounties: ![PROCESSOR_ROLE, ADMIN_ROLE].includes(form?.role)
            ? []
            : form?.lienSearchDefaultCounties
        }
    };
  }

  return {
    endpoint: endpoint,
    requestBody: { ...form }
  };
};

export const setItemsForEntityCrud = (entityName, response, user = {}) => {
  if (entityName === 'Users') {
    const responseCopy = [...response];
    const userIndex = responseCopy.findIndex(res => res?._id === user?._id);
    if (userIndex !== -1) {
      // Remove the user from its current position and place it at the start
      const [currentUser] = responseCopy.splice(userIndex, 1);
      return [currentUser, ...responseCopy];
    }
    return responseCopy;
  }
  return [...response]; // Response generated by API on first Render
};

export function isUserOrOrganizationEntity(entityName) {
  return entityName === 'Users' || entityName === 'Organizations';
}

export const addOrUpdateObjectInArray = (array = [], arrayOfObjectsToBePushed, primaryKey = '_id', addToTop = true) => {
  if (!arrayOfObjectsToBePushed) return array;

  if (!Array.isArray(arrayOfObjectsToBePushed)) arrayOfObjectsToBePushed = [arrayOfObjectsToBePushed];

  let arrayToReturn = [...array];

  arrayOfObjectsToBePushed.forEach(object => {
    const existingIndex = array.findIndex(o => o[primaryKey] === object[primaryKey]);
    if (existingIndex !== -1) {
      arrayToReturn = arrayToReturn.map(o => (o[primaryKey] === object[primaryKey] ? object : o));
    } else {
      arrayToReturn = addToTop ? [object, ...array] : [...array, object];
    }
  });

  return arrayToReturn;
};

export const getDateFromInputString = dateString => {
  try {
    return new Date(dateString.split('-').join('/')).toISOString();
  } catch (e) {
    return null;
  }
};

export const getFormattedDate = date => {
  var dd = date.getDate();
  var mm = date.getMonth() + 1; //January is 0!
  var yyyy = date.getFullYear();
  if (dd < 10) {
    dd = '0' + dd;
  }
  if (mm < 10) {
    mm = '0' + mm;
  }

  return yyyy + '-' + mm + '-' + dd;
};

export const downloadFileFromBase64 = ({ data, fileName, mimeType }) => {
  const downloadLink = document.createElement('a');
  downloadLink.href = mimeType ? `data:${mimeType};base64,${data}` : data;
  downloadLink.target = '_self';
  downloadLink.download = fileName;

  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
};

/**
 * Format bytes as human-readable text.
 *
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 *
 * @return Formatted string.
 */
export const humanFileSize = (bytes, si = false, dp = 1) => {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + ' B';
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

  return bytes.toFixed(dp) + ' ' + units[u];
};

export const sortedServices = (services, sortOption) => {
  const selectedOption = SERVICES_SORT_BY_OPTIONS.find(({ label, key }) => label === sortOption)?.key;

  if (selectedOption === 'alphabetical') {
    services.sort((serviceA, serviceB) =>
      serviceA?.fileName
        ?.trim()
        ?.toLowerCase()
        ?.localeCompare(serviceB?.fileName?.trim()?.toLowerCase())
    );
  } else {
    services.sort((serviceA, serviceB) => {
      return new Date(serviceA?.[selectedOption]).getTime() - new Date(serviceB?.[selectedOption]).getTime();
    });
  }

  return services;
};

export const filterServices = (services, filters, dateFilters, isDateFilterDisabled) => {
  let filteredServices = [];

  const startDate = new Date(dateFilters['Start Date'])?.getTime();
  const endDate = new Date(dateFilters['End Date'])?.getTime();

  let assignedProcessors = filters?.find(filter => filter.key === 'assignedProcessors')?.children;
  let organizations = filters?.find(filter => filter.key === 'organization')?.children;
  let urgency = filters?.find(filter => filter?.key === 'urgency')?.children;

  filteredServices = services?.filter(
    service =>
      (organizations?.length ? organizations.includes(service?.organization?.name) : true) &&
      (assignedProcessors?.length
        ? isAnyItemCommon(assignedProcessors, getAllProcessorsFormOrders(service?.orders))
        : true) &&
      (urgency?.length ? isAnyItemCommon(urgency, getAllRushValuesFromOrders(service?.orders)) : true) &&
      (!isDateFilterDisabled && areStartDateAndEndDateValid(startDate, endDate)
        ? serviceCreatedAt(service) >= startDate && serviceCreatedAt(service) <= endDate
        : true)
  );

  return filteredServices;
};

const isAnyItemCommon = (list1, list2) => {
  return list2.some(item => list1.includes(item));
};

const serviceCreatedAt = service => {
  return new Date(service?.createdAt)?.getTime();
};

const areStartDateAndEndDateValid = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return false;
  }

  if (isNaN(startDate) || isNaN(endDate)) {
    return false;
  }

  return true;
};

const getAllProcessorsFormOrders = orders => {
  const allProcessors = [];

  orders.forEach(order => {
    allProcessors.push(...order?.assignedProcessors.map(({ name }) => name));
  });

  return allProcessors;
};

const getAllRushValuesFromOrders = orders => {
  const allRushValues = [];

  orders.forEach(order => {
    allRushValues.push(order?.rush ? 'Rush' : 'General');
  });

  return allRushValues;
};

export const updateDateFiltersValue = factor => {
  if (factor === 'Last Week') {
    const lastWeek = moment().subtract(1, 'week');
    return [
      lastWeek
        .clone()
        .startOf('isoWeek')
        .format('YYYY-MM-DD'),
      lastWeek
        .clone()
        .endOf('isoWeek')
        .format('YYYY-MM-DD')
    ];
  }

  if (factor === 'This Week') {
    return [
      moment()
        .startOf('isoWeek')
        .format('YYYY-MM-DD'),
      moment()
        .endOf('isoWeek')
        .format('YYYY-MM-DD')
    ];
  }

  if (factor === 'Last Month') {
    return [
      moment(
        moment()
          .startOf('month')
          .subtract(1, 'month')?._d
      )?.format('YYYY-MM-DD'),
      moment(
        moment()
          .endOf('month')
          .subtract(1, 'month')?._d
      )?.format('YYYY-MM-DD')
    ];
  }

  if (factor === 'This Month') {
    return [
      moment(moment().startOf('month')?._d)?.format('YYYY-MM-DD'),
      moment(moment().endOf('month')?._d)?.format('YYYY-MM-DD')
    ];
  }
};

export const updatedServiceFiltereFields = (servicesData, user) => {
  let serviceRelatedFilters = cloneDeep(servicesData);

  if (isClient(user?.role)) {
    serviceRelatedFilters = serviceRelatedFilters.filter(filter => !filter.excludeForClientRole);
  }

  /*   if (user?.role === PROCESSOR_ROLE) {
    const processorsIndex = serviceRelatedFilters.findIndex(service => service?.key === 'assignedProcessors');

    serviceRelatedFilters[processorsIndex] = {
      ...serviceRelatedFilters[processorsIndex],
      options: serviceRelatedFilters[processorsIndex].options.filter(o => o.value === user._id),
      values: serviceRelatedFilters[processorsIndex].values.filter(v => v === user._id)
    };
  } */

  return serviceRelatedFilters;
};

export const getFullAddress = service => {
  if (!service) return '';

  const addressFields = ['propertyAddress', 'city', 'state'];
  const fullAddress = addressFields
    .map(field =>
      field !== 'state'
        ? service[field]
        : USA_STATES.find(({ name }) => name === service[field]?.toUpperCase())?.abbreviation || service[field]
    )
    .filter(value => value?.trim())
    .join(', ');
  if (!service['zipCode']) return fullAddress;
  return `${fullAddress} ${service['zipCode']}`;
};

export const formatEmailList = emails => {
  if (!Array.isArray(emails) || emails.length === 0) {
    return 'N/A';
  }

  if (emails.length === 1) {
    return emails[0];
  }

  if (emails.length === 2) {
    return `${emails[0]} and ${emails[1]}`;
  }

  return `${emails.slice(0, -1).join(', ')} and ${emails[emails.length - 1]}`;
};

export const replaceDatesInJson = obj => {
  if (Array.isArray(obj)) {
    return obj.map(item => replaceDatesInJson(item));
  } else if (typeof obj === 'object' && obj !== null) {
    return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, replaceDatesInJson(value)]));
  } else if (typeof obj === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(obj)) {
    return getDateFromInputString(obj);
  }
  return obj;
};

export function validateEmail(email) {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

/** Matches email addresses inside free-form text (e.g. Special Instructions). */
export const EMAIL_IN_TEXT_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

export function extractEmailsFromText(text) {
  if (!text || typeof text !== 'string') return [];
  const matches = text.match(EMAIL_IN_TEXT_REGEX) || [];
  return [...new Set(matches.filter(email => validateEmail(email)))];
}

export function textContainsEmail(text) {
  return extractEmailsFromText(text).length > 0;
}

/** Who submitted the order / service — for Share Report reference context. */
export function getServiceSubmitter(service) {
  if (!service) return { name: '', email: '' };

  if (service.requestingPersonName || service.requestingPersonEmail) {
    return {
      name: service.requestingPersonName || '',
      email: service.requestingPersonEmail || ''
    };
  }

  if (service.isIndividualService && (service.individual?.name || service.individual?.email)) {
    return {
      name: service.individual?.name || '',
      email: service.individual?.email || ''
    };
  }

  return {
    name: service.modifiedBy?.name || '',
    email: service.modifiedBy?.email || ''
  };
}

export const blobToBase64 = blob => {
  const reader = new FileReader();
  reader.readAsDataURL(blob);
  return new Promise(resolve => {
    reader.onloadend = () => {
      resolve(reader.result);
    };
  });
};

export async function getBlobFromBlobURL(blobUrl) {
  const response = await fetch(blobUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch blob: ${response.status} ${response.statusText}`);
  }
  return await response.blob();
}

export function calculateDateTimeRange(startDT, endDT, rangeType, direction) {
  const startDateTime = new Date(startDT);
  const endDateTime = new Date(endDT);

  const oneDay = 24 * 60 * 60 * 1000; // Milliseconds in one day

  let newStartDateTime, newEndDateTime;

  switch (rangeType) {
    case 'Day':
      if (direction === 'Previous') {
        newStartDateTime = new Date(startDateTime.getTime() - oneDay);
        newEndDateTime = new Date(endDateTime.getTime() - oneDay);
      } else if (direction === 'Next') {
        newStartDateTime = new Date(startDateTime.getTime() + oneDay);
        newEndDateTime = new Date(endDateTime.getTime() + oneDay);
      }
      break;

    case 'Week':
      if (direction === 'Previous') {
        newStartDateTime = new Date(startDateTime.getTime() - 7 * oneDay);
        newEndDateTime = new Date(endDateTime.getTime() - 7 * oneDay);
      } else if (direction === 'Next') {
        newStartDateTime = new Date(startDateTime.getTime() + 7 * oneDay);
        newEndDateTime = new Date(endDateTime.getTime() + 7 * oneDay);
      }
      break;

    case 'Month':
      let newYear = startDateTime.getFullYear();
      let newMonth = startDateTime.getMonth();

      if (direction === 'Previous') {
        newMonth--;
        if (newMonth === 0) {
          newMonth = 12;
          newYear--;
        }
      } else if (direction === 'Next') {
        newMonth++;
        if (newMonth === 13) {
          newMonth = 1;
          newYear++;
        }
      }

      // Create the first and last dates of the new month
      newStartDateTime = new Date(newYear, newMonth, 1);
      newEndDateTime = new Date(newYear, newMonth + 1, 0); // The last day of the month
      break;

    case 'Quarter':
      if (direction === 'Previous') {
        newStartDateTime = new Date(startDateTime.setMonth(startDateTime.getMonth() - 3));
        newEndDateTime = new Date(endDateTime.setMonth(endDateTime.getMonth() - 3));
      } else if (direction === 'Next') {
        newStartDateTime = new Date(startDateTime.setMonth(startDateTime.getMonth() + 3));
        newEndDateTime = new Date(endDateTime.setMonth(endDateTime.getMonth() + 3));
      }
      break;

    case 'Year':
      if (direction === 'Previous') {
        newStartDateTime = new Date(startDateTime.setFullYear(startDateTime.getFullYear() - 1));
        newEndDateTime = new Date(endDateTime.setFullYear(endDateTime.getFullYear() - 1));
      } else if (direction === 'Next') {
        newStartDateTime = new Date(startDateTime.setFullYear(startDateTime.getFullYear() + 1));
        newEndDateTime = new Date(endDateTime.setFullYear(endDateTime.getFullYear() + 1));
      }
      break;

    default:
      throw new Error('Invalid range type');
  }

  newStartDateTime.setHours(0, 0, 0, 0);
  newEndDateTime.setHours(23, 59, 59, 999);

  return {
    startDateTime: newStartDateTime,
    endDateTime: newEndDateTime
  };
}

export const getChainOrganizationLinkFilter = (organizationIds, isStaff) => {
  let queryParams = '';
  if (isStaff) {
    for (let i = 0; i < organizationIds.length; i++) {
      queryParams += `&organizationId=${organizationIds[i]}`;
    }
  }
  return queryParams;
};

export const getPercentage = (count, total) => {
  return isNaN((count / total) * 100) ? '0.00%' : ((count / total) * 100).toFixed(2) + '%';
};

export const individualOrdersOption = {
  value: INDIVIDUAL_ORDERS,
  label: 'Individuals',
  isEnabled: true
};

export async function copyToClipboard(value) {
  // Normalize input
  const text = String(value ?? '');

  // 1) Modern API in secure contexts
  try {
    if (
      typeof navigator !== 'undefined' &&
      navigator.clipboard &&
      typeof window !== 'undefined' &&
      window.isSecureContext
    ) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to fallback
  }

  // 2) Fallback using a temporary element + execCommand('copy')
  try {
    if (typeof document === 'undefined') return false; // Non-browser env

    const activeEl = document.activeElement;

    // Create a hidden, readonly textarea to avoid iOS zoom & keep focus behavior sane
    const ta = document.createElement('textarea');
    ta.value = text;

    // Make it as unobtrusive as possible
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-9999px';
    ta.style.left = '-9999px';
    ta.style.opacity = '0';
    ta.style.pointerEvents = 'none';

    document.body.appendChild(ta);

    // Select the text
    ta.focus();
    ta.select();

    // For iOS/Safari quirks
    ta.selectionStart = 0;
    ta.selectionEnd = ta.value.length;

    const ok = document.execCommand('copy');

    // Cleanup
    document.body.removeChild(ta);
    if (activeEl && typeof activeEl.focus === 'function') {
      activeEl.focus();
    }

    return ok;
  } catch {
    return false;
  }
}

// Helper to check if filter has values
export const hasFilterValues = (filter, values) => {
  if (filter.type === 'date') {
    if (filter.operator === FILTER_OPERATORS.TODAY_AND_BEFORE) {
      return true;
    }
    if (!values || typeof values !== 'object') return false;
    return Boolean(
      values.startDate ||
        values.endDate ||
        (values.$gte && values.$lte) ||
        (values.$gt && values.$lt)
    );
  }
  return Array.isArray(values) && values.length > 0;
};

// Helper to encode URL parameters
export const encodeUrlParams = params => new URLSearchParams(params).toString();

// Helpers for calculating business days and adding business days in tasks
const FLORIDA_BANK_HOLIDAYS_2026 = [
  '2026-01-01', // New Year's Day
  '2026-01-19', // Martin Luther King Jr. Day
  '2026-02-16', // Presidents' Day
  '2026-05-25', // Memorial Day
  '2026-06-19', // Juneteenth
  '2026-07-03', // Independence Day (observed)
  '2026-07-04', // Independence Day
  '2026-09-07', // Labor Day
  '2026-10-12', // Columbus Day/Indigenous Peoples' Day
  '2026-11-11', // Veterans Day
  '2026-11-26', // Thanksgiving Day
  '2026-12-25' // Christmas Day
];

// When excludeFridays is true, Friday (day 5) is not counted as a business day.
export const isBusinessDay = (date, excludeFridays = false) => {
  const day = date.day();
  const formattedDate = date.format('YYYY-MM-DD');
  const isHoliday2026 = FLORIDA_BANK_HOLIDAYS_2026.includes(formattedDate);

  if (day === 0 || day === 6 || isHoliday2026) return false; // 0 is Sunday, 6 is Saturday
  if (excludeFridays && day === 5) return false;
  return true;
};

const moveToPreviousBusinessDay = (date, excludeFridays = false) => {
  const d = date.clone();
  while (!isBusinessDay(d, excludeFridays)) {
    d.subtract(1, 'day');
  }
  return d;
};

// Helper to get date after adding business days
export const addBusinessDays = (startDate, businessDays, excludeFridays = false) => {
  const days = Number(businessDays);

  if (!Number.isInteger(days) || days < 0) return null;

  const date = startDate?.clone ? startDate.clone() : moment(startDate);
  let remainingDays = days;

  while (remainingDays > 0) {
    date.add(1, 'day');

    if (isBusinessDay(date, excludeFridays)) {
      remainingDays--;
    }
  }

  return date;
};

// Helper to get number of business days between two dates
export const getBusinessDaysDiff = (startDate, endDate, excludeFridays = false) => {
  if (!endDate || !endDate.isValid()) return 0;

  const start = startDate.clone().startOf('day');
  let end = endDate.clone().startOf('day');

  // If selected due date falls on a non-business day, treat it as the previous business day
  if (!isBusinessDay(end, excludeFridays)) {
    end = moveToPreviousBusinessDay(end, excludeFridays);
  }

  if (end.isSameOrBefore(start)) return 0;

  let diff = 0;
  const cursor = start.clone();
  cursor.add(1, 'day');

  while (cursor.isSameOrBefore(end)) {
    if (isBusinessDay(cursor, excludeFridays)) diff++;
    cursor.add(1, 'day');
  }

  return diff;
};

export const selectStyles = {
  container: base => ({
    ...base,
    minWidth: 160
  }),
  control: base => ({
    ...base,
    minHeight: 42,
    height: 42,
    borderColor: '#e5e7ea'
  }),
  menu: base => ({
    ...base,
    zIndex: 9999999999
  })
};
